import {effect, Injectable, Signal, signal} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {List, Map as ImmutableMap} from "immutable";
import {ALL_GENDERS, AllowPrivateMessages, ClientUser, MeUser, Roles, Sex} from "shared/user";
import {TransportService} from "./transport.service";
import {
    AccountWillDeleteWarning,
    AnnouncementEffect,
    ChatsReceivedEffect,
    EffectsService,
    LowBalanceUpcomingChargeEffect,
    NewMessageInRoomEffect,
    NewRoomCreatedEffect,
    PremiumUpsellEffect,
    ReconnectingEffect,
    ServerRoomClosedEffect,
    SnackBarMessageEffect,
    SocketDisconnectedError,
    UnpaidRoomEffect,
    WarningMessageEffect
} from "./effects.service";
import {
    BaseCommandMessage,
    BaseResponseMessage,
    COMMAND_BAN_ROOM,
    COMMAND_BLOCK_USER,
    COMMAND_CREATE_ROOM,
    COMMAND_GET_ANNOUNCEMENT,
    COMMAND_GET_CHATS,
    COMMAND_GET_MESSAGES,
    COMMAND_REMOVE_MESSAGES,
    COMMAND_SEND_TO,
    COMMAND_SHOW_WARNING,
    COMMAND_SUBSCRIBE,
    COMMAND_UNSUBSCRIBE,
    COMMAND_UPDATE_ROOM,
    COMMAND_USER_CONNECTED_OR_DISCONNECTED,
    COMMAND_USER_KICKED_FROM_ROOM,
    ComplaintNamespace,
    DeleteAccountNamespace,
    ErrorResponseMessage,
    GetAnnouncementNamespace,
    GetChatsCommandNamespace,
    RemoveMessageNamespace,
    RoomAdministration,
    RoomsNamespace,
    SendToCommandNamespace,
    ShowWarningNamespace,
    SubscribeOnChatNamespace,
    UserBlockingCommandNamespace,
    UserConnectedOrDisconnectedCommandNamespace,
    UserDataUpdatedCommandNamespace
} from "shared/commands";
import {ConnectCommandNamespace} from "shared/commands/connect";
import {Errors, getMessageIdByError} from "shared/errors";
import {serviceUser} from "shared/serviceusers";
import {AnalyticsService} from "./analytics.service";
import {getTimeRemaining, isUserAdministrator, isUserModerator} from "shared/utils";
import {createMessage, Message, MessageTypes, ToType} from "shared/message";
import {LocalModeratorPermissions, RoomType, ServerRoom} from "shared/room";
import {
    errorMessageForCantEnterBySex,
    isInBrowser,
    isPremiumActive,
    maxRoomMessages,
    pluralize,
    PluralType
} from "../utils/clientutils";
import {IgnoredUsersService} from "./ignored-users.service";
import {UserSettingsService} from "./user.settings.service";
import {DisconnectResponseMessage, RemoteCommandCallback, RemoteCommandService} from "./remote.command.service";
import {UserInfoService} from "./user.info.service";
import {ChatInfo, MessageStates, MessageWrapper, OpenRoom, RoomId} from "../models/chat";
import {environment} from "../../environments/environment";
import {RoomClientUsersWrapper, UserRoomRole} from "../models/client.user";
import {Payments} from "../../../shared/commands/payments";
import {i18n} from "../../helpers/i18n";
import {Money} from "../../../shared/money";


import {FavoritesService} from "./favorites.service";
import {FavoriteDto, FavoriteKind} from "../../../shared/favorites";
import GetAnnouncementMessage = GetAnnouncementNamespace.GetAnnouncementMessage;
import ToManyImagesInMainChatResponse = SendToCommandNamespace.ToManyImagesInMainChatResponse;
import Complaint = ComplaintNamespace.Complaint;
import KickedErrorResponse = SubscribeOnChatNamespace.KickedErrorResponse;


@Injectable({
    providedIn: 'root'
})
export class ChatStateService {
    //TODO: Получается какой то god класс, стоит использоваться композиции
    //Например сделать отдельный клас для _roomsUsers с удобными методами
    //то же самое для _roomsMessages, _serverRooms, _openRooms (ну или объеденить это все в один для управления комнатами)
    //Так же возможно стоит убрать отсюда методы для отправки данных, а стоит просто брать экземпляр _remoteCommandService и уже у него вызывать нужный метод
    //хотя это не будет работать для сложных команд как например получение списка комнат, отправка сообщений и т.д.

    private reconnects = 0

    private _roomsUsers: ImmutableMap<RoomId, List<RoomClientUsersWrapper>> = ImmutableMap()
    private _roomsMessages: ImmutableMap<RoomId, List<MessageWrapper>> = ImmutableMap()

    private _serverRooms: List<ChatInfo> = List()
    private _openRooms: List<OpenRoom> = List()

    private _roomsOrder: Array<string>

    private _currentTabIndex = signal(0)
    private _currentRoom = signal<OpenRoom>(null)
    private _currentRoomMessages = signal<List<MessageWrapper>>(List())
    private _currentRoomUsers = signal<List<RoomClientUsersWrapper>>(List())

    public allUnread: number = 0

    private _mainRoomName: string
    private _blockListRoomName: string
    private _adminsRoomName: string

    private _extraInfo: ConnectCommandNamespace.ExtraInfo

    private isReconnected = false
    private isExit = false

    private _paidRoomPrice = signal<number>(399)
    private _imageServers = signal<Array<ConnectCommandNamespace.ImageServer>>([])

    public loader = signal(true)

    private remoteServiceCallbacks = new Array<RemoteCommandCallback<any>>()

    constructor(private _socketService: TransportService, private _userInfoService: UserInfoService, private _remoteCommandService: RemoteCommandService, private _effectsService: EffectsService, private _analyticsService: AnalyticsService, private _ignoredUsersService: IgnoredUsersService, private _userSettingsService: UserSettingsService, private _translateService: TranslateService, private _favoritesService: FavoritesService) {
        this.subscribeOnMessages();

        if (isInBrowser()) {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    if (!this._socketService.isConnected()) {
                        this.reconnects = 0;
                        this.reconnect();
                    }
                }
            });
        }

        effect(() => {
            const favs = this._favoritesService.favorites();
            this.syncFavoritesWithAllRooms(favs);
        });
    }

    get serverRooms(): List<ChatInfo> {
        return this._serverRooms
    }

    get openRooms(): List<OpenRoom> {
        return this._openRooms
    }

    get paidRoomPrice(): Signal<number> {
        return this._paidRoomPrice
    }

    get imageServers(): Signal<Array<ConnectCommandNamespace.ImageServer>> {
        return this._imageServers
    }

    get roomsOrder(): Array<string> {
        return this._roomsOrder
    }

    get currentTabIndex(): Signal<number> {
        return this._currentTabIndex
    }

    get mainRoomName(): string {
        return this._mainRoomName;
    }

    get session() {
        return this._userInfoService.session
    }

    get me(): MeUser {
        return this._userInfoService.me
    }

    get connected(): boolean {
        return this._remoteCommandService.isConnected
    }

    get invisible(): boolean {
        return this.me.invisible
    }

    get adminsRoomName(): string {
        return this._adminsRoomName
    }

    get blockListRoomName(): string {
        return this._blockListRoomName
    }

    get extraInfo(): ConnectCommandNamespace.ExtraInfo {
        return this._extraInfo
    }

    get currentRoom(): Signal<OpenRoom> {
        return this._currentRoom
    }

    reconnect(): boolean {
        if (this.reconnects >= 3) return false
        this.isReconnected = true

        this.reconnects++
        this._effectsService.clear()
        this._effectsService.broadcast(new ReconnectingEffect())
        return this._remoteCommandService.reconnect()
    }

    isUserIgnored(login: string): boolean {
        return this._ignoredUsersService.isUserIgnored(login)
    }

    ignoreUser(login: string) {
        this._ignoredUsersService.ignoreUser(login)
    }

    swapIgnoreUser(login: string) {
        this._ignoredUsersService.swapIgnoreUser(login)
    }

    get ignoreUserChanges(): Signal<string> {
        return this._ignoredUsersService.onChange
    }

    complaint(login: string, message: string, complaint: Complaint) {
        const roomTitle = this.currentRoom()?.title ?? ''
        this._remoteCommandService.complaint(login, message, complaint, roomTitle)

        this.ignoreUser(login)
    }

    roomUsers(roomId: RoomId): List<ClientUser> {
        return this._roomsUsers.get(roomId)
    }

    roomMessages(roomId: RoomId): List<MessageWrapper> {
        return this._roomsMessages.get(roomId, List())
    }

    get currentRoomUsers(): Signal<List<RoomClientUsersWrapper>> {
        return this._currentRoomUsers
    }

    get currentRoomMessages(): Signal<List<MessageWrapper>> {
        return this._currentRoomMessages
    }

    getOpenRoomIndexByRoomId(roomId: RoomId): number {
        return this._openRooms.findIndex((room) => {
            return room.roomId === roomId
        })
    }

    findOpenRoomById(roomId: RoomId): OpenRoom | undefined {
        return this._openRooms.find((room) => {
            return room.roomId === roomId
        })
    }

    getServerRoom(title: string): ChatInfo {
        if (!title) {
            return undefined
        }

        const lowerCase = title.toLowerCase()

        const index = this._serverRooms.findIndex((room) => {
            return room.title?.toLowerCase() === lowerCase
        })

        if (index >= 0) {
            return this._serverRooms.get(index)
        } else {
            return undefined
        }
    }

    getCurrentRoomIfItPaid(): ChatInfo {
        const openRoom = this.currentRoom()

        if (!openRoom || !openRoom.fromServer) {
            return null
        }

        const serverRoom = this.getServerRoom(openRoom.title)

        if (!serverRoom || !serverRoom.type) {
            return null
        }

        return serverRoom
    }

    getServerRoomId(title: string): RoomId | undefined {
        return this.getServerRoom(title)?.roomId
    }

    getOpenRoomIdForServerRoom(title: string): RoomId | undefined {
        if (!title) {
            return undefined
        }

        const index = this._openRooms.findIndex((room) => {
            return room.title?.toLowerCase() === title.toLowerCase() && room.fromServer
        })

        if (index >= 0) {
            return this._openRooms.get(index).roomId
        } else {
            return undefined
        }
    }

    getOpenRoomIdForPrivateRoom(title: string): RoomId | undefined {
        if (!title) {
            return undefined
        }

        const index = this._openRooms.findIndex((room) => {
            return room.title?.toLowerCase() === title.toLowerCase() && !room.fromServer
        })

        if (index >= 0) {
            return this._openRooms.get(index).roomId
        } else {
            return undefined
        }
    }

    selectRoom(index: number): OpenRoom {
        if (index >= this._openRooms.size) {
            console.log("Wrong selected index: ", index, this._openRooms.toArray())
            index = 0
        }

        const room = this._openRooms.get(index)

        if (!room) {
            console.log(`Open room with index ${index} not found `)
            return null
        }

        //Теоретически можно подписаться на _currentTabIndex и в подпищике проставлять _currentTabIndex и _currentRoomUsers,
        //но мне кажется это будет излишне запутанным, так как обновление текущих данных будет в разных местах
        this._currentRoom.set(room)
        this._currentRoomMessages.set(this._roomsMessages.get(room.roomId, List()))
        this._currentRoomUsers.set(this._roomsUsers.get(room.roomId, List()))

        this._currentTabIndex.set(index)

        return room
    }

    createNewPrivateRoomAndAddItToRooms(roomName: string, members: Array<ClientUser>, disconnected: boolean = false): ChatInfo {
        let room = new ChatInfo(roomName, false, RoomType.PRIVATE_ROOM, this.me.login, ALL_GENDERS, false, true, false, '', [], true, maxRoomMessages(roomName),);
        if (disconnected) {
            room = {...room, disconnected: disconnected}
        }

        this.updateOpenRoomsCollection(this._openRooms.push(room))

        this.addRoomMembers(room.roomId, ...members)

        return room
    }

    closeOpenedRoom(index: number) {
        let openRoom = this._openRooms.get(index)

        if (!openRoom) return;

        if (!openRoom.closable) return

        this.decreaseUnread(openRoom.roomId)

        this.updateOpenRoomsCollection(this._openRooms.remove(index))

        const serverRoom = this.getServerRoom(openRoom.title)

        if (serverRoom) {
            this.unsubscribeRoom(serverRoom.title);
        } else {
            this.clearRoomMembers(openRoom.roomId)
        }

        if (openRoom.fromServer) {
            //Для локальных чатов оставляем историю чтоб после переоткрытия показать ее снова
            this.removeRoomMessages(openRoom.roomId)
        }

        const current = this._currentTabIndex()

        if (current > this._openRooms.size - 1) {
            this.selectRoom(current - 1);
        } else {
            this.selectRoom(current)
        }
    }

    decreaseUnread(roomId: RoomId) {
        const index = this.getOpenRoomIndexByRoomId(roomId)

        if (index < 0) return

        let chat = this._openRooms.get(index)

        this.allUnread = this.allUnread - chat.unreadMessages;

        chat = {...chat, unreadMessages: 0}

        this.updateOpenRoomsCollection(this._openRooms.set(index, chat))

        this.updateUnreadInWindowTitle()
    }

    getOpenRoomByIndex(index: number): OpenRoom {
        return this._openRooms.get(index)
    }

    getOpenRoomIndexWithServerRoomFilter(roomTitle: string): number {
        return this.getOpenRoomIndexWithFilter(roomTitle, (room) => {
            return room.fromServer
        })
    }

    getOpenRoomIndexWithPrivateRoomFilter(roomTitle: string): number {
        return this.getOpenRoomIndexWithFilter(roomTitle, (room) => {
            return !room.fromServer
        })
    }

    openServerRoom(roomTitle: string): number {
        const room = this.getServerRoom(roomTitle)

        if (!room) return 0

        this.updateOpenRoomsCollection(this._openRooms.push(room))

        return this.openRooms.size - 1
    }

    clearData() {
        this._effectsService.clear()
        this._userInfoService.clearData()
        this.clearSessionStoredData()
        this.clearPremiumUpsellTimer()
    }

    addRoomMessages(roomId: RoomId, ...messages: Array<MessageWrapper>) {
        let messagesList = this._roomsMessages.get(roomId, List<MessageWrapper>())

        let room = this._openRooms.find((room) => {
            return room.roomId === roomId
        })

        if (!room) return

        messagesList = messagesList.push(...messages)

        while (messagesList.size > room.maxMessagesCount) {
            let indexForRemove = 0
            if (messagesList.get(indexForRemove).pinned) {
                indexForRemove = 1
            }
            messagesList = messagesList.remove(indexForRemove)
        }

        this.updateRoomsMessagesCollection(roomId, messagesList)
    }

    removeMessage(roomId: RoomId, messageId: string) {
        let messages = this._roomsMessages.get(roomId)

        if (!messages) return

        const index = messages.findLastIndex((message) => {
            return message.id === messageId
        })

        if (index >= 0) {
            messages = messages.remove(index)

            this.updateRoomsMessagesCollection(roomId, messages)
        }
    }

    removeMessages(roomId: RoomId, messagesIds: Array<string>) {
        let messages = this._roomsMessages.get(roomId)

        if (!messages) return

        messages = messages.filter(message => {
            //Возвращаем только те ид которых не найдено в ид для удаления
            return messagesIds.indexOf(message.id) < 0
        })

        this.updateRoomsMessagesCollection(roomId, messages)
    }

    replaceMessage(roomId: RoomId, message: MessageWrapper): boolean {
        let messages = this._roomsMessages.get(roomId)

        if (!messages) return true

        const index = messages.findLastIndex((m) => {
            return m.id === message.id
        })

        if (index >= 0) {
            messages = messages.set(index, message)

            this.updateRoomsMessagesCollection(roomId, messages)

            return true
        }

        return false
    }

    findConnectedUser(login: string): ClientUser | null {
        const index = this.getOpenRoomIndexWithServerRoomFilter(this.mainRoomName)
        if (index < 0) return null

        const room = this.getOpenRoomByIndex(index)
        if(!room) return null

        const lowerLogin = login.toLowerCase()
        return this.roomUsers(room.roomId).find((user) => {
            return user.login.toLowerCase() === lowerLogin
        })
    }

    updateUnreadInWindowTitle() {
        if (!isInBrowser()) return
        let title = this._translateService.instant('chat_title')

        if (!environment.production) {
            title = ''
        }

        if (this.allUnread <= 0) {
            document.title = title;
        } else {
            document.title = `(${this.allUnread}) ${title}`;
        }
    }

    openServerRoomAndSubscribe(roomTitle: string, password: string = '', callback?: (success: boolean) => void): number {
        //Защита от повторного открытия комнаты
        let index = this.getOpenRoomIndexWithServerRoomFilter(roomTitle)
        if (index >= 0) {
            if (!environment.production) {
                console.warn(`Пытаемся подписаться и открыть комнату которая уже открыта ${roomTitle}`, new Error())
            }
            return -1
        }

        index = this.openServerRoom(roomTitle)
        this.subscribeOnRoom(roomTitle, password, callback)
        return index
    }

    exit() {
        this.isExit = true
        this._remoteCommandService.disconnect()
        this.clearData()
    }

    async deleteAccount() {
        const command = new DeleteAccountNamespace.DeleteAccountCommand()

        let response = await this._remoteCommandService.sendCommandWithCallback(command)

        const success = response.success

        if (success) {
            const newUser = {
                ...this.me,
                willDelete: (response as DeleteAccountNamespace.DeleteAccountSuccessResponse).willDelete
            }
            this._userInfoService.updateUser(newUser)
        }

        return success
    }

    async cancelDeleteAccount() {
        const command = new DeleteAccountNamespace.CancelDeleteAccountCommand()

        let response = await this._remoteCommandService.sendCommandWithCallback(command)

        const success = response.success

        if (success) {
            const newUser = {...this.me, willDelete: null}
            this._userInfoService.updateUser(newUser)
        }

        return success
    }

    async blockedUsers(room: string) {
        const command = new RoomAdministration.BlockedUsersCommand(room);
        return this._remoteCommandService.sendCommandWithCallback(command)
    }

    async kickFromRoom(title: string, login: string, reasonId: string, howLong: number) {
        const command = new RoomAdministration.KickFromRoomCommand(title, login, reasonId, howLong);
        return this._remoteCommandService.sendCommandWithCallback(command)
    }

    async grantPaidRoomModeratorRole(room: string, login: string): Promise<BaseResponseMessage> {
        const command = new RoomAdministration.GrantPaidRoomModeratorRoleCommand(room, login);
        return this._remoteCommandService.sendCommandWithCallback(command)
    }

    async takeOffPaidRoomModeratorRole(room: string, login: string): Promise<BaseResponseMessage> {
        const command = new RoomAdministration.TakeOffRoomModeratorRoleCommand(room, login);
        return this._remoteCommandService.sendCommandWithCallback(command)
    }

    async assignRoomPinnedMessage(room: string, message: string): Promise<BaseResponseMessage> {
        const command = new RoomAdministration.UpdateRoomPinnedMessage(room, message);
        return this._remoteCommandService.sendCommandWithCallback(command)
    }

    public requestRooms() {
        const command = new BaseCommandMessage(COMMAND_GET_CHATS);

        this._socketService.sendMessage(command);
    }

    public requestAnnouncement() {
        const command = new GetAnnouncementMessage();

        this._socketService.sendMessage(command);
    }

    private getOpenRoomIndexWithFilter(roomTitle: string, filter: (room: OpenRoom) => boolean) {
        const lowerTitle = roomTitle.toLowerCase()
        return this._openRooms.findIndex(room => {
            return room.title?.toLowerCase() === lowerTitle && filter(room)
        })
    }

    private getRoomIndexWithToTypeFilter(title: string, type: ToType): RoomId | undefined {
        let roomId: RoomId

        if (type == ToType.CHAT) {
            roomId = this.getOpenRoomIdForServerRoom(title)
        } else if (type == ToType.USER) {
            roomId = this.getOpenRoomIdForPrivateRoom(title)
        }

        return roomId
    }

    private onUserConnected(user: ClientUser) {
        const lowerLogin = user.login.toLowerCase();
        this._openRooms.forEach((room) => {
            const members = this._roomsUsers.get(room.roomId);
            if (members) {
                const index = members.findIndex(m => m.login.toLowerCase() === lowerLogin);
                if (index !== -1) {
                    this.processUserArrival(room.roomId, user);
                }
            }
        });
    }

    private onUserDisconnected(user: ClientUser) {
        this._openRooms.forEach((room) => {
            this.processUserDeparture(room.roomId, user);
        });
    }

    private processUserArrival(roomId: string, user: ClientUser) {
        const lowerLogin = user.login.toLowerCase();

        // Если зашёл анонимный пользователь, который у нас в избранном, обновляем сохранённый
        // регистр его логина на актуальный (источник истины — последний вход). Иначе после его
        // выхода в списке отобразился бы устаревший регистр (см. updateGuestFavoriteLogin).
        if (!user.registered) {
            this._favoritesService.updateGuestFavoriteLogin(user.login);
        }

        const members = this._roomsUsers.get(roomId);

        const alreadyOnline = members && members.some(m => m.login.toLowerCase() === lowerLogin && m.online);

        this.addRoomMembers(roomId, user);

        if (!alreadyOnline) {
            this.sendConnectedOrDisconnectedMessage(user, roomId, true);
        }

        // Если это личная комната с юзером, то меняем статус
        const roomIndex = this.getOpenRoomIndexWithPrivateRoomFilter(user.login);
        if (roomIndex >= 0) {
            const room = this._openRooms.get(roomIndex);
            if (!room.fromServer && room.disconnected) {
                this.updateOpenRoomsCollection(this._openRooms.set(roomIndex, {...room, disconnected: false}));
            }
        }
    }

    private processUserDeparture(roomId: string, user: ClientUser) {
        const lowerLogin = user.login.toLowerCase();
        let members = this._roomsUsers.get(roomId);

        if (!members) return;

        const index = members.findIndex(m => m.login.toLowerCase() === lowerLogin);
        if (index === -1) return;

        const member = members.get(index);
        // Если он уже офлайн, то ничего не делаем (чтобы не спамить сервисными сообщениями если пришло и unsubscribe и disconnect)
        if (!member.online) return;

        const room = this.getServerRoomById(roomId);
        const isPrivate = !room && this.getOpenRoomIndexWithPrivateRoomFilter(user.login) >= 0;
        const isMainRoom = room && room.type === RoomType.MAIN_ROOM;
        const fav = this._favoritesService.getFavorite(user.login);

        if (isPrivate || (isMainRoom && fav)) {
            // В приватных чатах или Лобби для избранных не удаляем, а просто помечаем как офлайн
            const clientUserWithPrettyLogin: ClientUser = {
                login: fav ? fav.login : member.login,
                sex: member.registered ? member.sex : undefined, // Убираем пол для анонимов
                roles: member.roles,
                registered: member.registered,
                invisible: member.invisible,
                allowPrivateMessages: member.allowPrivateMessages,
                premiumUntil: member.premiumUntil,
                boostInUsersList: member.boostInUsersList,
                willDelete: member.willDelete
            };

            const newMember = new RoomClientUsersWrapper(clientUserWithPrettyLogin, member.isMe, member.userType, false);
            const newMembers = members.set(index, newMember);
            this._roomsUsers = this._roomsUsers.set(roomId, newMembers);
            if (this._currentRoom()?.roomId === roomId) {
                this._currentRoomUsers.set(newMembers);
            }
        } else {
            // В обычных комнатах удаляем
            const newMembers = members.remove(index);
            this._roomsUsers = this._roomsUsers.set(roomId, newMembers);
            if (this._currentRoom()?.roomId === roomId) {
                this._currentRoomUsers.set(newMembers);
            }
        }

        this.sendConnectedOrDisconnectedMessage(user, roomId, false);

        if (isMainRoom) {
            // Обновляем статус комнаты, если это личка, только если это выход из главной комнаты.
            // Потому что возможна ошибка, что пользователь вышел из какой-то из комнаты, в которой я нахожусь,
            // и мы пометили личку как disconnected, хотя он реально находится в чате
            const roomIndex = this.getOpenRoomIndexWithPrivateRoomFilter(user.login);
            if (roomIndex >= 0) {
                const room = this._openRooms.get(roomIndex);
                if (!room.fromServer && !room.disconnected) {
                    this.updateOpenRoomsCollection(this._openRooms.set(roomIndex, {...room, disconnected: true}));
                }
            }
        }
    }


    private syncFavoritesWithAllRooms(favorites: List<FavoriteDto>) {
        const mainRoomId = this.getServerRoomId(this.mainRoomName);
        const favoritesMap = new Map(favorites.map(f => [f.login.toLowerCase(), f]));
        const favoriteLogins = new Set(favoritesMap.keys());

        this._roomsUsers = this._roomsUsers.map((members, roomId) => {
            let roomChanged = false;
            const isMainRoom = roomId === mainRoomId;

            let updatedMembers = members.map(member => {
                const fav = favoritesMap.get(member.login.toLowerCase());
                if (!fav) return member;

                const favIsRegistered = fav.kind === FavoriteKind.Registered;
                const currentRealUser = member.getRealUser();
                
                const newLogin = currentRealUser.login !== fav.login ? fav.login : currentRealUser.login;
                const newRegistered = currentRealUser.registered !== favIsRegistered ? favIsRegistered : currentRealUser.registered;
                
                let newSex = currentRealUser.sex;
                if (favIsRegistered && fav.sex !== undefined && currentRealUser.sex !== fav.sex) {
                    newSex = fav.sex;
                }

                if (!member.online && !favIsRegistered && currentRealUser.sex !== undefined) {
                    newSex = undefined;
                }

                if (newLogin !== currentRealUser.login || newRegistered !== currentRealUser.registered || newSex !== currentRealUser.sex) {
                    roomChanged = true;
                    return new RoomClientUsersWrapper(
                        {
                            ...currentRealUser,
                            login: newLogin,
                            registered: newRegistered,
                            sex: newSex
                        },
                        member.isMe,
                        member.userType,
                        member.online
                    );
                }
                return member;
            });

            if (isMainRoom) {
                favorites.forEach(fav => {
                    const lowerLogin = fav.login.toLowerCase();
                    if (!updatedMembers.some(m => m.login.toLowerCase() === lowerLogin)) {
                        let foundUser: RoomClientUsersWrapper | undefined;
                        this._roomsUsers.forEach((roomMembers) => {
                            if (foundUser) return;
                            foundUser = roomMembers.find(m => m.login.toLowerCase() === lowerLogin);
                        });

                        const newUser = new RoomClientUsersWrapper(
                            {
                                login: fav.login,
                                sex: fav.kind === FavoriteKind.Registered ? (foundUser?.sex ?? fav.sex) : undefined,
                                roles: foundUser?.roles ?? [],
                                registered: fav.kind === FavoriteKind.Registered,
                                invisible: foundUser?.invisible ?? false,
                                allowPrivateMessages: foundUser?.allowPrivateMessages ?? AllowPrivateMessages.all,
                                premiumUntil: foundUser?.premiumUntil ?? 0,
                                boostInUsersList: foundUser?.boostInUsersList ?? false,
                                willDelete: foundUser?.willDelete ?? 0
                            },
                            foundUser?.isMe ?? false,
                            UserRoomRole.usualUser,
                            false
                        );
                        updatedMembers = updatedMembers.push(newUser);
                        roomChanged = true;
                    }
                });

                const filteredMembers = updatedMembers.filter(m => m.online || favoriteLogins.has(m.login.toLowerCase()));
                if (filteredMembers.size !== updatedMembers.size) {
                    updatedMembers = filteredMembers;
                    roomChanged = true;
                }
            }

            if (roomChanged) {
                if (this._currentRoom()?.roomId === roomId) {
                    this._currentRoomUsers.set(updatedMembers);
                }
                return updatedMembers;
            }
            return members;
        });
    }


    private sendConnectedOrDisconnectedMessage(user: ClientUser, roomId: RoomId, connected: Boolean) {
        if (!this._userSettingsService.showServiceMessages) return;

        //В чат с блокировками не пишем кто вошел и вышел
        const blockedChatRoomId = this.getServerRoomId(this.blockListRoomName)
        if (blockedChatRoomId && blockedChatRoomId == roomId) return;

        let message: MessageWrapper;

        const phrase = this._translateService.instant(`service_phrases.${connected ? 'enter' : 'exit'}.${user.sex === Sex.female ? 'female' : 'male'}`, {'login': user.login})

        message = new MessageWrapper(createMessage(phrase, serviceUser, MessageTypes.text), MessageStates.sent, true);

        this.addRoomMessages(roomId, message)
    }


    private createMessageAboutDisconnect() {
        const phrase = this._translateService.instant('connection_restored_after_disconnection')
        return new MessageWrapper(createMessage(phrase, serviceUser, MessageTypes.text), MessageStates.sent, true);
    }

    private unsubscribeRoom(room: string) {
        const command = new SubscribeOnChatNamespace.UnsubscribeMessage(room);

        this._socketService.sendMessage(command);
    }

    private requestMessages(room: string) {
        const command = new RoomsNamespace.GetMessages(room);

        this._socketService.sendMessage(command);
    }

    private increaseUnreadIfNeed(roomId: RoomId, message: MessageWrapper) {
        if (this.currentRoom().roomId !== roomId || document.visibilityState !== 'visible') {
            let index = this.getOpenRoomIndexByRoomId(roomId)

            if (index >= 0) {
                let room = this._openRooms.get(index)
                room = {...room, unreadMessages: room.unreadMessages + 1}
                this.updateOpenRoomsCollection(this._openRooms.set(index, room))

                this.allUnread = this.allUnread + 1;

                this.updateUnreadInWindowTitle()
            }
        }
    }

    private removeRoomMessages(roomId: RoomId) {
        if (this._roomsMessages.has(roomId)) {
            this.updateRoomsMessagesCollection(roomId, List())
        }
    }

    private subscribeOnRoom(chatName: string, password: string = '', callback?: (success: boolean) => void) {
        const command = new SubscribeOnChatNamespace.SubscribeMessage(chatName, password);

        this._remoteCommandService.sendCommandWithCallback(command).then((response) => {
            this.onSubscribeMessage(response)
            if (callback) {
                callback(response.success)
            }
        })
    }

    private updateOpenRoomsCollection(rooms: List<OpenRoom>) {
        //TODO: Сделать по аналогии с updateRoomsMessagesCollection, передаем комнату и здесь обновляем коллекцию
        this._openRooms = rooms
        this.saveChatDataToStorage()
    }

    private updateRoomsMessagesCollection(roomId: string, messages: List<MessageWrapper>) {
        this._roomsMessages = this._roomsMessages.set(roomId, messages)

        if (this._currentRoom()?.roomId === roomId) {
            this._currentRoomMessages.set(messages)
        }

        this.saveChatDataToStorage()
    }

    private saveChatDataToStorage() {
        if (!isInBrowser()) return
        const startMark = 'start_saving_data'
        const endMark = 'end_saving_data'

        performance.mark(startMark)
        let rooms = this._openRooms.filter(value => {
            if (value.fromServer) {
                return !this.getServerRoom(value.title)?.isPassword
            } else {
                return true
            }

        }).map((value) => {
            const stored: StoredRoom = {
                title: value.title,
                fromServer: value.fromServer,
                id: value.roomId
            }
            if (!value.fromServer) {
                const members = this._roomsUsers.get(value.roomId)
                if (members) {
                    stored.participants = members
                        .filter(m => !m.isMe)
                        .map(m => m.getRealUser())
                        .toArray()
                }
            }
            return stored
        })

        window.sessionStorage.setItem('rooms', JSON.stringify(rooms));

        let messages = {}

        //Пока сохраняются сообщения только в открытых комнатах
        //возможно в будущем сделаю чтоб и для закрытых тоже сохранялось
        rooms.forEach((room) => {
            if (this._roomsMessages.has(room.id)) {
                const currentMessages = this._roomsMessages.get(room.id)
                if (!currentMessages || currentMessages.isEmpty()) {
                    messages[room.id] = []
                } else {
                    if (room.fromServer) {
                        //для серверных комнат сохраняем только последнее сообщение, так как история хранится на сервере
                        messages[room.id] = new Array(currentMessages?.last()?.getRealMessage())
                    } else {
                        messages[room.id] = currentMessages //не сохраняем сервисные сообщения
                            .filter((value) => !value.service)
                            .map((value) => value.getRealMessage())
                    }
                }
            } else {
                messages[room.id] = []
            }
        })

        window.sessionStorage.setItem('messages', JSON.stringify(messages))

        window.sessionStorage.setItem('current-room', this.currentRoom()?.roomId)
        performance.mark(endMark)

        const measurement = performance.measure('saving_data', startMark, endMark)

        if (measurement.duration > 100) {
            this._analyticsService.savingDataTooLong(measurement.duration, this._openRooms.size)
        }
    }

    private clearSessionStoredData() {
        if (!isInBrowser()) return
        window.sessionStorage.removeItem('rooms')
        window.sessionStorage.removeItem('messages')
        window.sessionStorage.removeItem('current-room')
    }

    private onSocketDisconnected(message: DisconnectResponseMessage) {
        if (this.isExit) return

        this.isReconnected = false
        if (isInBrowser() && document.visibilityState === 'hidden' && this.reconnects >= 2) {
            // ничего не делаем, ждем, когда вкладка станет активной, чтоб попробовать переподключиться
        } else if (message.statusCode >= Errors.RECONNECTION_NOT_POSSIBLE) {
            this.clearData()
            this._analyticsService.serverDisconnected();
            this._effectsService.broadcast(new SocketDisconnectedError(message.statusCode))
        } else {
            if (!this.reconnect()) {
                this.clearData()
                this._effectsService.broadcast(new SocketDisconnectedError(message.statusCode))
            }
        }
        //Нет необходимости отписываться, так как сервис создастся только раз и не уничтожается
    }

    private subscribeOnMessages() {
        this.setCallbackForRemoteCommandService(RemoteCommandService.SERVICE_COMMAND_SOCKET_DISCONNECT, (message: DisconnectResponseMessage) => {
            this.onSocketDisconnected(message)
        })
        this.setCallbackForRemoteCommandService(ConnectCommandNamespace.COMMAND_CONNECT, this.onConnectCommand)
        this.setCallbackForRemoteCommandService(COMMAND_GET_CHATS, this.onGetChatsCommand)
        this.setCallbackForRemoteCommandService(COMMAND_GET_ANNOUNCEMENT, this.onGetAnnouncement)
        this.setCallbackForRemoteCommandService(COMMAND_CREATE_ROOM, this.onRoomCreated)
        this.setCallbackForRemoteCommandService(COMMAND_BAN_ROOM, this.onRoomClosed)
        this.setCallbackForRemoteCommandService(COMMAND_USER_CONNECTED_OR_DISCONNECTED, this.onUserConnectedOrDisconnected)
        this.setCallbackForRemoteCommandService(COMMAND_GET_MESSAGES, this.onGetMessages)
        this.setCallbackForRemoteCommandService(COMMAND_SEND_TO, this.onSendTo)
        this.setCallbackForRemoteCommandService(COMMAND_SUBSCRIBE, this.onSubscribeMessage)
        this.setCallbackForRemoteCommandService(COMMAND_UNSUBSCRIBE, this.onUnSubscribeMessage)
        this.setCallbackForRemoteCommandService(COMMAND_BLOCK_USER, this.onUserBlocked)
        this.setCallbackForRemoteCommandService(COMMAND_REMOVE_MESSAGES, this.onMessageRemove)
        this.setCallbackForRemoteCommandService(COMMAND_SHOW_WARNING, this.showWarning)
        this.setCallbackForRemoteCommandService(COMMAND_USER_KICKED_FROM_ROOM, this.onUserKicked)
        this.setCallbackForRemoteCommandService(COMMAND_UPDATE_ROOM, this.onUpdateRoom)
        this.setCallbackForRemoteCommandService(COMMAND_UPDATE_ROOM, this.onUpdateRoom)
        this.setCallbackForRemoteCommandService(Payments.COMMAND_DEPOSIT_MONEY, this.onDepositMoney)
        this.setCallbackForRemoteCommandService(UserDataUpdatedCommandNamespace.COMMAND, this.onUserDataUpdated)
    }

    private setCallbackForRemoteCommandService(command: string, callback: RemoteCommandCallback<any>) {
        const boundCallback = callback.bind(this)

        this.remoteServiceCallbacks.push(boundCallback)
        this._remoteCommandService.subscribeOnCommand(command, boundCallback)
    }

    private removeCallbacksFromRemoteCommandService() {
        this.remoteServiceCallbacks.forEach(callback => {
            this._remoteCommandService.unsubscribeCallback(callback)
        })

        this.remoteServiceCallbacks.length = 0
    }

    private clearCollections() {
        this._serverRooms = List()
        this._openRooms = List()
        this._roomsUsers = ImmutableMap()
        this._roomsMessages = ImmutableMap()
    }

    private onConnectCommand(message: BaseResponseMessage) {
        if (message.success) {
            this.isExit = false
            this.reconnects = 0

            //Чистим все что было
            this.clearCollections()

            const response = message as ConnectCommandNamespace.Response;
            this._extraInfo = response.extra
            this._userInfoService.setData(response.user, response.settings, response.session)
            this._userSettingsService.onUserConnected(response.user, response.settings)

            if (response.extra && response.extra.paid_room_price_cents) {
                this._paidRoomPrice.set(response.extra.paid_room_price_cents)
            }

            if (response.extra && response.extra.image_servers) {
                const subdomains = response.extra.image_servers/*[{subdomain: "12-images", priority: 1},{subdomain: "13-images", priority: 1},{subdomain: "14-images", priority: 1},{subdomain: "21-images", priority: 10}, {subdomain: "22-images", priority: 10}]*/
                this._imageServers.set(subdomains)
            }

            if (response.notifications) {
                response.notifications.forEach(notification => {
                    if (notification.type === ConnectCommandNamespace.NOTIFICATION_UNPAID_ROOM) {
                        this._effectsService.broadcast(new UnpaidRoomEffect(notification.metadata.roomTitle));
                    } else if (notification.type === ConnectCommandNamespace.NOTIFICATION_LOW_BALANCE_UPCOMING_CHARGE) {
                        this._effectsService.broadcast(new LowBalanceUpcomingChargeEffect(notification.metadata.roomTitle, notification.metadata.daysLeft));
                    } else if (notification.type === ConnectCommandNamespace.NOTIFICATION_PM_SETTING_RESET) {
                        this._effectsService.broadcast(new SnackBarMessageEffect(
                            this._translateService.instant('private_messages_reset_notification'),
                            this._translateService.instant('close'),
                            5000
                        ));
                    }
                });
            }

            this.maybeShowPremiumUpsell(response.user, response.extra);

            this.requestAnnouncement();
            this.requestRooms();
            this._favoritesService.loadFavorites().then();
        }
    }

    /**
     * Ненавязчивое напоминание про премиум для зарегистрированных пользователей без премиума.
     * Полностью клиентская логика (localStorage) — сервер не хранит частоту показов,
     * это сделано намеренно, чтобы фичу было легко отключить/выкинуть, если не даст эффекта.
     * Показ отложен: сразу после входа уже есть объявление и уведомления сервера,
     * напоминание в тот же момент в них потеряется.
     */
    private static PREMIUM_UPSELL_STORAGE_PREFIX = 'premium_upsell_last_shown_'
    private static PREMIUM_UPSELL_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000
    private static PREMIUM_UPSELL_SHOW_DELAY_MS = 90 * 1000
    private premiumUpsellTimer: ReturnType<typeof setTimeout> | null = null

    private maybeShowPremiumUpsell(user: MeUser, extra: ConnectCommandNamespace.ExtraInfo) {
        this.clearPremiumUpsellTimer()

        if (!isInBrowser()) return
        if (!user.registered || isPremiumActive(user)) return
        if (!extra?.buy_premium_enabled) return

        const storageKey = ChatStateService.PREMIUM_UPSELL_STORAGE_PREFIX + user.login.toLowerCase()
        const lastShown = Number(window.localStorage.getItem(storageKey)) || 0

        if (Date.now() - lastShown < ChatStateService.PREMIUM_UPSELL_COOLDOWN_MS) return

        window.localStorage.setItem(storageKey, String(Date.now()))
        this.premiumUpsellTimer = setTimeout(() => {
            this.premiumUpsellTimer = null
            this._effectsService.broadcast(new PremiumUpsellEffect())
        }, ChatStateService.PREMIUM_UPSELL_SHOW_DELAY_MS)
    }

    private clearPremiumUpsellTimer() {
        if (this.premiumUpsellTimer) {
            clearTimeout(this.premiumUpsellTimer)
            this.premiumUpsellTimer = null
        }
    }

    private onUserDataUpdated(data: UserDataUpdatedCommandNamespace.Response) {
        const updatedUser = data.user;
        const lowerUpdatedLogin = updatedUser.login.toLowerCase();

        // Обновляем текущего пользователя, если это я
        if (this.me?.login.toLowerCase() === lowerUpdatedLogin) {
            const newMe = {...this.me, ...updatedUser};
            this.updateMe(newMe);
        }

        // Обновляем пользователя во всех комнатах
        this._roomsUsers = this._roomsUsers.map((members, roomId) => {
            const index = members.findIndex(m => m.login.toLowerCase() === lowerUpdatedLogin);
            if (index !== -1) {
                const oldMember = members.get(index);
                const newMember = new RoomClientUsersWrapper(updatedUser, oldMember.isMe, oldMember.userType, oldMember.online);
                const newMembers = members.set(index, newMember);
                
                if (this._currentRoom()?.roomId === roomId) {
                    this._currentRoomUsers.set(newMembers)
                }
                return newMembers;
            }
            return members;
        });
    }

    private isRoomCritical(room: ServerRoom): boolean {
        return room.type === RoomType.MAIN_ROOM || room.type === RoomType.MODERATORS_ROOM
    }

    private getServerRoomById(roomId: string): ChatInfo {
        return this._serverRooms.find((room) => {
            return room.roomId === roomId
        })
    }

    private getUserRoomPermissions(room: ChatInfo, login: string): Array<LocalModeratorPermissions|Roles> {
        return room.moderators.find((it) => {
            return it.login === login
        })?.permissions
    }

    private addRoomMembers(roomId: string, ...members: ClientUser[]) {
        const room = this.getServerRoomById(roomId)
        const me = this.me
        let currentMembers = this._roomsUsers.get(roomId, List<RoomClientUsersWrapper>())

        members.forEach((clientUser: ClientUser) => {
            const lowerLogin = clientUser.login.toLowerCase();
            const isMe = me.login.toLowerCase() === lowerLogin;

            let roomRole = UserRoomRole.usualUser
            if (room) {
                const localPermissions = this.getUserRoomPermissions(room, clientUser.login)
                if (room.type === RoomType.PAID_ROOM) {
                    if (room.createdBy === clientUser.login) {
                        roomRole = UserRoomRole.localAdministrator
                    } else if (localPermissions) {
                        roomRole = UserRoomRole.localModerator
                    }
                }
            }

            const isOnline = isMe || (clientUser instanceof RoomClientUsersWrapper ? clientUser.online : true);

            const index = currentMembers.findIndex(m => m.login.toLowerCase() === lowerLogin);
            if (index !== -1) {
                // Если пользователь уже есть, обновляем его данные и сохраняем статус
                const newMember = new RoomClientUsersWrapper(clientUser, isMe, roomRole, isOnline);
                currentMembers = currentMembers.set(index, newMember);
            } else {
                const newMember = new RoomClientUsersWrapper(clientUser, isMe, roomRole, isOnline);
                currentMembers = currentMembers.push(newMember);
            }
        });

        this._roomsUsers = this._roomsUsers.set(roomId, currentMembers)

        if (this._currentRoom()?.roomId === roomId) {
            this._currentRoomUsers.set(currentMembers)
        }
    }

    private clearRoomMembers(roomId: string) {
        this._roomsUsers = this._roomsUsers.set(roomId, List())
    }

    private updateServerRoomInfo(room: ChatInfo) {
        const index = this._serverRooms.findIndex((serverRoom: ChatInfo) => {
            return serverRoom.roomId === room.roomId
        })

        if (index >= 0) {
            this._serverRooms = this._serverRooms.set(index, room)
        } else {
            this._serverRooms = this._serverRooms.push(room)
        }
    }

    private onGetChatsCommand(message: GetChatsCommandNamespace.Response) {
        this._serverRooms = List()

        message.chats.forEach(room => {
            if (room.type === RoomType.MAIN_ROOM) {
                this._mainRoomName = room.title
            } else if (room.type === RoomType.MODERATORS_ROOM) {
                this._adminsRoomName = room.title
            } else if (room.type === RoomType.SERVICE_ROOM) {
                this._blockListRoomName = room.title
            }

            const roomInfo = new ChatInfo(room.title.trim(), true, room.type, room.createdBy, room.allowedGenders, room.password, room.isPaid ,room.allowOnlyRegistered, room.pinnedMessage, room.moderators, !this.isRoomCritical(room), maxRoomMessages(room.title));

            this.updateServerRoomInfo(roomInfo)
            this.addRoomMembers(roomInfo.roomId, ...room.members)

            return roomInfo
        })

        this._roomsOrder = [this._mainRoomName, this._adminsRoomName, this._blockListRoomName]

        let storedRooms: Array<StoredRoom> = null
        let storedMessagesRaw = null
        let currentRoom: string = null

        if (isInBrowser()) {
            storedRooms = JSON.parse(window.sessionStorage.getItem('rooms')) as Array<StoredRoom>
            storedMessagesRaw = JSON.parse(window.sessionStorage.getItem('messages'))
            currentRoom = window.sessionStorage.getItem('current-room') as string
        }

        let storedMessages: ImmutableMap<string, Array<Message>> = ImmutableMap()

        if (storedMessagesRaw) {
            storedMessages = ImmutableMap(Object.entries(storedMessagesRaw))
        }

        if (!this.isReconnected || !storedRooms || !currentRoom) {
            this.openServerRoomAndSubscribe(this._mainRoomName)

            if (isUserModerator(this.me) || isUserAdministrator(this.me)) {
                const room = this.getServerRoom(this.adminsRoomName)
                if (room) {
                    this.openServerRoomAndSubscribe(room.title)
                }
            }

            setTimeout(() => {
                this.selectRoom(0);
            });
        } else {
            //Этот код должен работать и для восстановления сообщений комнат которые были закрыты,
            //но сообщения закрытых комнат пока не сохраняются
            if (storedMessages) {
                storedMessages.filter((value, key) => {
                    //восстанавливаем только приватные сообщения
                    //максимально тупо!!! нужно бы придумать что-то получше
                    return key.endsWith('-private')
                }).forEach((value, key) => {
                    const messages = value.map((m) => new MessageWrapper(m, MessageStates.sent))
                    this.updateRoomsMessagesCollection(key, List.of(...messages))
                })
            }

            storedRooms.forEach((value) => {
                if (value.fromServer) {
                    const room = this.getServerRoom(value.title)

                    if (room) {
                        this.openServerRoomAndSubscribe(room.title, '', () => {
                            this.addRoomMessages(value.id, this.createMessageAboutDisconnect())
                        })
                    }
                } else {
                    const members = new Array<ClientUser>(this.me)

                    const user = this.findConnectedUser(value.title)

                    let disconnected = true
                    if (user) {
                        members.push(user)
                        disconnected = false
                    } else if (value.participants?.length) {
                        value.participants.forEach(p => {
                            members.push(new RoomClientUsersWrapper(p, false, UserRoomRole.usualUser, false))
                        })
                    }

                    let room = this.createNewPrivateRoomAndAddItToRooms(value.title, members, disconnected)

                    this.addRoomMessages(room.roomId, this.createMessageAboutDisconnect())
                }
            })

            let index = this.getOpenRoomIndexByRoomId(currentRoom)

            if (index < 0 || index >= this._openRooms.size) {
                index = 0
            }

            this.selectRoom(index)
        }

        this._effectsService.broadcast(new ChatsReceivedEffect())

        if (this.me.willDelete) {
            let diff = this.me.willDelete - Date.now()

            if (diff <= 0) {
                diff = 60 * 60 * 1000 // 1 час
            }

            this._effectsService.broadcast(new AccountWillDeleteWarning(diff))
        }

        this.isReconnected = false
    }

    private onGetAnnouncement(message: GetAnnouncementNamespace.Response) {
        const announcement = message.announcement
        this._effectsService.broadcast(new AnnouncementEffect(announcement.title, announcement.html))
    }

    private onRoomCreated(response: BaseResponseMessage) {
        if (!response.success) {
            const data = response as ErrorResponseMessage

            let message: string = this._translateService.instant(getMessageIdByError(data.error))

            this._effectsService.broadcast(new SnackBarMessageEffect(message, this._translateService.instant(i18n.close), 3000))
        } else {
            const data = response as RoomsNamespace.CreateRoomResponse

            const roomInfo = data.room

            const room = new ChatInfo(
                roomInfo.title,
                true,
                roomInfo.type,
                roomInfo.createdBy,
                roomInfo.allowedGenders,
                roomInfo.password,
                roomInfo.isPaid,
                roomInfo.allowOnlyRegistered,
                roomInfo.pinnedMessage,
                roomInfo.moderators,
                true,
                maxRoomMessages(roomInfo.title)
            );


            //TODO: Здесь стоит сделать метод через который будут добавляться комнаты в массив
            this._serverRooms = this._serverRooms.push(room)
            //Просто сетим пустой список пользователей для комнаты
            this.clearRoomMembers(room.roomId)

            this._effectsService.broadcast(new NewRoomCreatedEffect(room, roomInfo.createdBy))
        }
    }

    private onRoomClosed(response: BaseResponseMessage) {
        if (!response.success) {
            const error = response as ErrorResponseMessage

            const message = getMessageIdByError(error.error)

            if (message) {
                this._effectsService.broadcast(new SnackBarMessageEffect(this._translateService.instant(message), this._translateService.instant(i18n.close), 3000))
            }
            return
        }

        const data = response as RoomsNamespace.BanRoomResponse

        const index = this._serverRooms.findIndex((chat) => {
            return chat.title === data.title;
        });

        this._serverRooms = this._serverRooms.remove(index)

        const roomId = this.getServerRoomId(data.title)

        this.removeRoomMessages(roomId)

        this._effectsService.broadcast(new ServerRoomClosedEffect(data.title))
    }

    private onUserConnectedOrDisconnected(data: UserConnectedOrDisconnectedCommandNamespace.Response) {
        if (data.isConnected) {
            this.onUserConnected(data.user)
        } else {
            this.onUserDisconnected(data.user)
        }
    }

    updateMe(user: MeUser) {
        const wasPremium = isPremiumActive(this.me);
        this._userInfoService.updateUser(user)
        const isPremiumNow = isPremiumActive(user);

        if (!wasPremium && isPremiumNow) {
            this._favoritesService.loadFavorites().then();
        }
    }

    private formatElapsedTime(time: number, includeSeconds: boolean = false): string {
        const data = getTimeRemaining(time)

        let result = ''

        if (data.days > 0) {
            result += `${data.days} ${pluralize(data.days, PluralType.DAYS, this._translateService)} `
        }

        if (data.hours > 0) {
            result += `${data.hours} ${pluralize(data.hours, PluralType.HOURS, this._translateService)} `
        }

        if (data.minutes > 0) {
            result += `${data.minutes} ${pluralize(data.minutes, PluralType.MINUTES, this._translateService)} `
        }

        if (includeSeconds && data.seconds > 0) {
            result += `${data.seconds} ${pluralize(data.seconds, PluralType.SECONDS, this._translateService)} `
        }

        return result
    }

    private onSendTo(response: BaseResponseMessage) {
        if (!response.success) {
            const error = response as SendToCommandNamespace.ErrorResponse

            let roomId: RoomId = this.getRoomIndexWithToTypeFilter(error.to, error.toType)

            if (!roomId) {
                return;
            }

            this.replaceMessage(roomId, new MessageWrapper(error.message, response.success ? MessageStates.sent : MessageStates.error, false))

            let message: string

            if (error.error == Errors.TO_MANY_IMAGES_IN_MAIN_CHAT) {
                const e = error as ToManyImagesInMainChatResponse

                let period = this.formatElapsedTime(e.imagePerPeriod, true)

                message = this._translateService.instant(getMessageIdByError(error.error), {'period': period})
            } else {
                message = this._translateService.instant(getMessageIdByError(error.error))
            }

            if (message) {
                this._effectsService.broadcast(new SnackBarMessageEffect(message, this._translateService.instant(i18n.close), 10_000))
            }
        } else {
            let data = response as SendToCommandNamespace.Response

            const messageFromMe = data.from === this.me.login;

            if (messageFromMe) {
                // Если сообщение от меня, то нужно только проставить подтверждение
                // Для этого нам нужно найти нужный чат, если он есть

                let roomId: RoomId = this.getRoomIndexWithToTypeFilter(data.to, data.toType)

                if (!roomId) {
                    return;
                }

                const replaced = this.replaceMessage(roomId, new MessageWrapper(data.message, data.success ? MessageStates.sent : MessageStates.error, false))

                if (!replaced) {
                    // Пришло сообщение с картинкой, его нужно добавить в массив
                    this.addRoomMessages(roomId, new MessageWrapper(data.message, MessageStates.sent, false));
                }
                return;
            }

            if (this._ignoredUsersService.isUserIgnored(data.from)) {
                return;
            }

            let roomName: string;

            if (data.to === this.me.login && data.toType == ToType.USER) { // Сообщение конкретно мне
                roomName = data.from;

                let index = this.getOpenRoomIndexWithPrivateRoomFilter(data.from);

                if (index < 0) {
                    //Значит такого чата еще нет, нужно создать
                    const members: Array<ClientUser> = [this.me];

                    let user: ClientUser = this.findConnectedUser(data.from);
                    if (user) {
                        members.push(user)
                    }

                    this.createNewPrivateRoomAndAddItToRooms(roomName, members)
                }
            } else if (data.toType == ToType.CHAT) {
                roomName = data.to
            }

            if (!roomName) {
                console.warn("Unknown room name", roomName)
                return;
            }

            let roomId = this.getRoomIndexWithToTypeFilter(roomName, data.toType)

            if (!roomId) {
                console.warn("Unknown roomId", roomName)
                return;
            }

            const messageWrapper = new MessageWrapper(data.message, MessageStates.sent, false);

            this.addRoomMessages(roomId, messageWrapper)

            this.increaseUnreadIfNeed(roomId, messageWrapper);

            this._effectsService.broadcast(new NewMessageInRoomEffect(roomName, roomId, messageWrapper))
        }
    }

    private onGetMessages(data: RoomsNamespace.GetMessagesResponse) {
        if (!data.success) {
            //TODO: Я хз как это возникает, нужно будет как то разобраться
            return
        }

        const room = this.getServerRoom(data.title)

        if (!room) {
            return
        }

        if (room.type === RoomType.MAIN_ROOM) {
            this.loader.set(false)
        }

        const messages = data.messages.map(value => {
            return new MessageWrapper({...value, message: value.message}, MessageStates.sent, false)
        })

        if (room.pinnedMessage) {
            messages.push(this.createPinnedMessage(room))
        }

        const roomId = this.getOpenRoomIdForServerRoom(data.title)

        this.addRoomMessages(roomId, ...messages)

        if (messages.length > 0) {
            //this._effectsService.broadcast(new NewMessageInRoomEffect(data.title, messages[messages.length - 1]))
        }
    }

    private createPinnedMessage(room: ChatInfo): MessageWrapper {
        if (!room.pinnedMessage) {
            return null
        }

        const clientUser: ClientUser = {
            allowPrivateMessages: AllowPrivateMessages.disabled,
            invisible: false,
            login: room.createdBy,
            registered: true,
            roles: [],
            sex: Sex.male

        }

        return new MessageWrapper(createMessage(room.pinnedMessage, clientUser, MessageTypes.text, true), MessageStates.sent, false)
    }

    private onSubscribeMessage(response: BaseResponseMessage) {
        if (!response.success) {
            const subscribeError = response as SubscribeOnChatNamespace.SubscribeErrorResponse;
            let message: string

            if (subscribeError.error === Errors.WRONG_PASSWORD) {
                message = this._translateService.instant('wrong_password')
            } else if (subscribeError.error === Errors.CAN_ENTER_ONLY) {
                //Если я парень и меня не пустили в комнату, значит комната только для девушек
                message = errorMessageForCantEnterBySex(this._translateService, this.me.sex)
            } else if (subscribeError.error === Errors.ENTER_ALLOWED_ONLY_TO_REGISTERED) {
                message = this._translateService.instant('allowed_only_to_registered_error')
            } else if (subscribeError.error === Errors.KICKED_FROM_ROOM) {
                // noinspection UnnecessaryLocalVariableJS
                const room = (response as KickedErrorResponse).room
                message = this._translateService.instant('kicked_from_room_error', {room: room})
            }

            if (message) {
                this._effectsService.broadcast(new SnackBarMessageEffect(message, this._translateService.instant(i18n.close), 3000))
            }

            const roomIndexForClose = this.getOpenRoomIndexWithServerRoomFilter(subscribeError.room)

            if (roomIndexForClose >= 0) {
                this.closeOpenedRoom(roomIndexForClose)
            }
        } else {
            const data = response as SubscribeOnChatNamespace.SubscribeResponse
            const roomId = this.getServerRoomId(data.chat)

            if (roomId) {
                this.processUserArrival(roomId, data.user);
            }

            if (!this.me) {
                //TODO: Хз как так получается, нужно выяснить
                return
            }

            //Запрашиваем сообщения только если на чат подписался сам юзер, когда другие на него подписываются нам не нужно запрашивать сообщения
            if (data.user.login === this.me.login) {
                this.requestMessages(data.chat);
            }
        }
    }

    private onUnSubscribeMessage(data: SubscribeOnChatNamespace.UnsubscribeResponse) {
        const roomId = this.getServerRoomId(data.room);

        if (roomId) {
            this.processUserDeparture(roomId, data.user);
        }
    }

    private onUserBlocked(data: UserBlockingCommandNamespace.Response) {
        let reason: string;

        switch (data.result) {
            case UserBlockingCommandNamespace.BlockResult.notFound:
                reason = 'block_error_reason_user_not_found';
                break;
            case UserBlockingCommandNamespace.BlockResult.userIsBot:
                reason = 'block_error_reason_user_bot_or_admin';
                break;
            case UserBlockingCommandNamespace.BlockResult.userIsAdmin:
                reason = 'block_error_reason_user_bot_or_admin';
                break;
            case UserBlockingCommandNamespace.BlockResult.wrongReason:
                reason = 'block_error_reason_unknown_reason';
                break;
            case UserBlockingCommandNamespace.BlockResult.dontHavePermissionToBlock:
                reason = 'block_error_reason_you_dont_have_permissions';
                break;
            case UserBlockingCommandNamespace.BlockResult.userAlreadyWarned:
                reason = 'block_error_reason_user_already_warned';
                break;
            case UserBlockingCommandNamespace.BlockResult.warned:
                reason = 'user_warned'
                break;
            case UserBlockingCommandNamespace.BlockResult.blocked:
                reason = 'user_blocked'
                break;
            default:
                reason = 'block_error_reason_unknown_reason';
        }

        let message = ''

        if (data.success) {
            message = this._translateService.instant(reason);

            //для успешной блокировки не отображаем сообщение
        } else {
            const blockUserError = this._translateService.instant('user_block_error_first_part', {user: data.user});
            const blockUserReason = this._translateService.instant(reason);

            message = `${blockUserError} ${blockUserReason}`

            this._effectsService.broadcast(new SnackBarMessageEffect(message, this._translateService.instant(i18n.close), 3000))
        }
    }

    private onUserKicked(data: RoomAdministration.KickFromRoomUserResponse) {
        const index = this.getOpenRoomIndexWithServerRoomFilter(data.room)

        if (index > 0) {
            this.closeOpenedRoom(index)
        }

        this._effectsService.broadcast(new WarningMessageEffect(this._translateService.instant('kicked_from_room', {room: data.room})))
    }

    private onUpdateRoom(data: RoomsNamespace.UpdateRoomResponse) {
        const roomInfo = data.room

        const room = new ChatInfo(
            roomInfo.title,
            true,
            roomInfo.type,
            roomInfo.createdBy,
            roomInfo.allowedGenders,
            roomInfo.password,
            roomInfo.isPaid,
            roomInfo.allowOnlyRegistered,
            roomInfo.pinnedMessage,
            roomInfo.moderators,
            true,
            maxRoomMessages(roomInfo.title)
        );

        const oldServerRoom = this.getServerRoomById(room.roomId)

        let needUpdatePinnedMessage = false
        if (oldServerRoom) {
            needUpdatePinnedMessage = oldServerRoom.pinnedMessage !== room.pinnedMessage
        }

        this.updateServerRoomInfo(room)

        let users = this._roomsUsers.get(room.roomId)?.toArray()
        if (users) {
            this.clearRoomMembers(room.roomId)
            this.addRoomMembers(room.roomId, ...users)
        }

        if (needUpdatePinnedMessage) {
            let messages = this._roomsMessages.get(room.roomId)

            if (messages) {
                const index = messages.findIndex((message) => {
                    // в комнате может быть только одно закрепленное сообщение
                    return message.pinned
                })

                if (index >= 0) {
                    messages = messages.remove(index)
                }
            } else {
                messages = List.of()
            }


            if (room.pinnedMessage) {
                messages = messages.push(this.createPinnedMessage(room))
            }

            this.updateRoomsMessagesCollection(room.roomId, messages)
        }

        const currentRoom = this._currentRoom()

        if(currentRoom.roomId === room.roomId) {
            // необходимо, чтобы перезапустить эффект, чтоб, обновить кнопку для администратора комнаты
            const r = {...currentRoom}
            this._currentRoom.set(r)
        }
    }

    private onDepositMoney(data: Payments.DepositMoneyResponse) {
        let me = {...this._userInfoService.me, money: data.money}

        this._userInfoService.updateUser(me)

        let textId = ''

        if(data.depositOn > 0) { // пополнение
            textId = i18n.your_balance_has_been_replenished_by
        } else { // списание
            textId = i18n.has_been_debited_from_you
        }

        this._effectsService.broadcast(new SnackBarMessageEffect(this._translateService.instant(textId, {amount: Money.fromCents(data.depositOn).format()}), this._translateService.instant(i18n.close), 6000))
    }

    private onMessageRemove(data: BaseResponseMessage) {
        if (!data.success) {
            const error = data as ErrorResponseMessage

            const message = getMessageIdByError(error.error)

            if (message) {
                this._effectsService.broadcast(new SnackBarMessageEffect(this._translateService.instant(message), this._translateService.instant(i18n.close), 3000))
            }
        } else {
            const response = data as RemoveMessageNamespace.RemoveMessagesResponse

            const roomId = this.getOpenRoomIdForServerRoom(response.chat);

            if (roomId) {
                this.removeMessages(roomId, response.messagesId)
            }
        }
    }

    private showWarning(data: ShowWarningNamespace.ShowWarningResponse) {
        this._effectsService.broadcast(new WarningMessageEffect(data.text))
    }
}

interface StoredRoom {
    title: string,
    fromServer: boolean,
    id: string,
    participants?: ClientUser[]
}
